import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { OpenAPI } from "simplydo/interfaces";
import { Button, Divider, Loader, Message } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import SettingActions from "components/lib/SettingActions";

import TermsCheckbox from "./TermsCheckbox";

const AgreeToTerms = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const reloadUser = useUserReload();
  const user = useAppSelector((state) => state.user);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const returnUrl = useMemo(() => {
    const next = searchParams.get("next") || "";
    try {
      return atob(next) || "/";
    } catch (e) {
      return "/";
    }
  }, [searchParams]);

  const [hasAgreed, setHasAgreed] = useState(false);
  const [agreedCustomTerms, setAgreedCustomTerms] = useState(false);
  const [organisation, setOrganisation] = useState<
    OpenAPI.Schemas["Organisation"] | null
  >(user?.ownerOrganisation ?? null);

  const [loading, setLoading] = useState(false);

  const requiresTermsAgreement = !hasAgreed;
  const requiresCustomTermsAgreement = organisation?.customTerms?.enabled
    ? !agreedCustomTerms
    : false;

  useEffect(() => {
    if (!organisation) {
      const code = util.code();
      if (!code) {
        setError("Something went wrong. Please try logging in again.");
        return;
      }
      api.organisations.getByCode(
        code,
        (org) => {
          setOrganisation(org);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [organisation]);

  const onAgree = useCallback(() => {
    if (!hasAgreed) {
      toast.error("Please agree to the terms before continuing");
      return;
    }
    setLoading(true);
    if (user) {
      api.auth.agreeToTerms(
        constants.termsVersion,
        user?.ownerOrganisation?.customTerms?.version ?? "",
        (...args: unknown[]) => {
          const legalAgreements = args[0] as typeof user.legalAgreements;
          dispatch(actions.user.agreeTerms(legalAgreements));
          setLoading(false);
          reloadUser();
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    } else {
      try {
        window.location.href = returnUrl;
      } catch (e) {
        toast.error("Something went wrong. Please try logging in again.");
      }
    }
  }, [hasAgreed, dispatch, reloadUser, user, returnUrl]);

  if (error) {
    return (
      <Message error>
        <Message.Header>{error}</Message.Header>

        <Divider hidden />
        <Message.Content>
          <Button
            size="small"
            content="Return to login"
            onClick={() => navigate("/login")}
          />
        </Message.Content>
      </Message>
    );
  }

  if (!organisation) {
    return <Loader active inline="centered" />;
  }

  return (
    <div
      style={{ maxHeight: "69vh", display: "flex", flexDirection: "column" }}
    >
      <div style={{ overflowY: "auto", paddingRight: 8 }}>
        <h3>{t("entry.terms.title")}</h3>
        <p>
          {user
            ? t("entry.terms.description")
            : t("entry.terms.newUserDescription")}
        </p>
        <TermsCheckbox
          organisation={organisation}
          agreed={hasAgreed}
          onAgree={(agreed) => setHasAgreed(agreed)}
          agreedCustomTerms={agreedCustomTerms}
          onAgreeCustomTerms={(agreed) => setAgreedCustomTerms(agreed)}
        />
      </div>
      <Divider hidden />
      <SettingActions>
        <Button
          primary
          content="Continue"
          onClick={onAgree}
          loading={loading}
          disabled={requiresTermsAgreement || requiresCustomTermsAgreement}
        />
      </SettingActions>
    </div>
  );
};

export default AgreeToTerms;
